import { i18n } from "@tellescope/i18n";
import type { Tool } from "@tellescope/ai-agent";
import type { Session } from "@tellescope/sdk";
import type { ZodObject } from "zod";
import {
  validateAggregationPipeline,
  formatAggregationResults,
  generateFieldDocumentation,
} from "./analytics-utilities";

/**
 * Input type for analytics tools - MongoDB aggregation pipeline
 */
interface AnalyticsToolInput {
  aggregation: Array<object>;
}

/**
 * Builds an analytics Tool for a specific Tellescope data model.
 *
 * This factory function creates a Tool that:
 * 1. Accepts MongoDB aggregation pipelines as input
 * 2. Validates the pipeline for security (no forbidden stages)
 * 3. Executes the aggregation via analytics_frames.custom_aggregation API
 * 4. Returns formatted results
 *
 * The Zod schema serves dual purposes:
 * - Provides field documentation to help the LLM construct valid queries
 * - Could be used for runtime validation (currently not enforced)
 *
 * @param modelName - Name of the Tellescope model (e.g., "endusers", "tickets")
 * @param schema - Zod schema defining available fields for this model
 * @param session - Tellescope SDK session for API calls
 * @returns Tool instance configured for analytics on this model
 *
 * @example
 * ```typescript
 * import { buildAnalyticsToolForModel } from '@tellescope/ai-agent-tellescope';
 * import { enduserAnalyticsSchema } from '@tellescope/ai-agent-tellescope/schemas';
 *
 * const enduserTool = buildAnalyticsToolForModel(
 *   "endusers",
 *   enduserAnalyticsSchema,
 *   session
 * );
 *
 * // Tool can now be used in an agent:
 * const agent = create_agent({
 *   name: "analytics-agent",
 *   selectionPrompt: "Answer analytics questions",
 *   tools: [enduserTool],
 *   getCompletion: createGetCompletion(session),
 * });
 * ```
 */
export function buildAnalyticsToolForModel<T extends ZodObject<any>>(
  modelName: string,
  schema: T,
  session: Session
): Tool {
  // Generate field documentation from schema for LLM context
  const fieldDocs = generateFieldDocumentation(schema);

  return {
    name: `query_${modelName}_analytics`,

    selectionPrompt: `Use this tool to query and analyze ${modelName} data. This tool accepts MongoDB aggregation pipelines to perform complex queries, grouping, counting, and statistical analysis.`,

    executionPrompt: `You are generating a MongoDB aggregation pipeline to query ${modelName} data.

${fieldDocs}

Generate a valid MongoDB aggregation pipeline. Common stages:
- $match: Filter documents (e.g., { "$match": { "status": "active" } })
- $group: Group and aggregate (e.g., { "$group": { "_id": "$status", "count": { "$sum": 1 } } })
- $sort: Sort results (e.g., { "$sort": { "count": -1 } })
- $limit: Limit results (e.g., { "$limit": 10 })
- $project: Select/transform fields (e.g., { "$project": { "name": 1, "email": 1 } })
- $count: Count documents (e.g., { "$count": "total" })

IMPORTANT:
- Use only the fields listed above
- DO NOT use forbidden stages: $out, $merge, $function, $accumulator
- Return ONLY the JSON object with "aggregation" key, no additional text or markdown

Example output:
{
  "aggregation": [
    { "$match": { "status": "active" } },
    { "$group": { "_id": "$status", "count": { "$sum": 1 } } },
    { "$sort": { "count": -1 } }
  ]
}`,

    validateInput: (input: AnalyticsToolInput): string[] => {
      const errors: string[] = [];

      // Validate aggregation exists and is an array
      if (!input.aggregation) {
        errors.push(i18n.t("Missing required field: aggregation", { ns: "ai-agent" }));
        return errors;
      }

      if (!Array.isArray(input.aggregation)) {
        errors.push(i18n.t("Aggregation must be an array of pipeline stages", { ns: "ai-agent" }));
        return errors;
      }

      if (input.aggregation.length === 0) {
        errors.push(i18n.t("Aggregation array cannot be empty", { ns: "ai-agent" }));
        return errors;
      }

      // Validate for forbidden stages
      const pipelineErrors = validateAggregationPipeline(input.aggregation);
      errors.push(...pipelineErrors);

      return errors;
    },

    execute: async (input: AnalyticsToolInput) => {
      // Execute aggregation via Tellescope SDK
      const response = await session.api.analytics_frames.custom_aggregation({
        modelName,
        aggregation: input.aggregation,
      });

      // Extract results from response (API returns { result: [...] })
      const results = response?.result ?? [];

      // Format results for LLM
      const summarizedOutput = formatAggregationResults(results, modelName);

      return {
        output: {
          modelName,
          aggregation: input.aggregation,
          results,
        },
        summarizedOutput,
      };
    },
  } as Tool;
}
